import type { GridApi } from 'ag-grid-community'
import { format, parseISO } from 'date-fns'
import { Download } from 'lucide-react'
import { useCallback, useState } from 'react'

import { Button } from '@/components/base/button'
import { Checkbox } from '@/components/base/checkbox'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/base/popover'
import { Text } from '@/components/base/text'
import { useI18n } from '@/i18n/i18n-react'
import { TrianglesMini } from '@/icons/triangles-mini'
import { clx } from '@/utils/clx'

import { type AnalyticsPeriodSearch, DeltaBadge, selectedPeriodStart } from '../analytics-shared'

export type Amount = { value: number; precision: number }

/** Major-unit number for an API amount (what the grid sorts on and the CSV exports). */
export const toMajor = (amount: Amount): number => amount.value / Math.pow(10, amount.precision)

export type RevenueReportGroupBy = 'hour' | 'day' | 'week' | 'month'

/** Row granularities offered per interval; mirrors `allowedRevenueReportGroupBy` in the API. */
export const allowedGroupBy = (interval: AnalyticsPeriodSearch['interval']): RevenueReportGroupBy[] => {
	switch (interval) {
		case 'day':
			return ['hour']
		case 'week':
			return ['day']
		case 'month':
			return ['day', 'week']
		case 'quarter':
			return ['day', 'week', 'month']
		case 'year':
			return ['week', 'month']
	}
}

/** The interval's default row granularity (mirrors the API's `getBucketType`). */
export const defaultGroupBy = (interval: AnalyticsPeriodSearch['interval']): RevenueReportGroupBy => {
	switch (interval) {
		case 'day':
			return 'hour'
		case 'week':
		case 'month':
			return 'day'
		case 'quarter':
			return 'week'
		case 'year':
			return 'month'
	}
}

/** Human label for a revenue-report bucket key (`yyyy-MM-dd HH:00`, `yyyy-MM-dd` or `yyyy-MM`). */
export function useBucketLabel(): (key: string, groupBy: RevenueReportGroupBy) => string {
	const { L } = useI18n()
	return useCallback(
		(key: string, groupBy: RevenueReportGroupBy) => {
			const start = parseISO(key)
			switch (groupBy) {
				case 'hour':
					return L.Date.DateAndTime(start.getTime())
				case 'day':
					return L.Date.ShortWithDate(start.getTime())
				case 'week': {
					const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6)
					return `${L.Date.Short(start.getTime())} – ${L.Date.Short(end.getTime())}`
				}
				case 'month':
					return L.Date.MonthAndYear(start.getTime())
			}
		},
		[L]
	)
}

/** CSV file name for a report, e.g. `report_2026-10-01_month.csv` (selected period start + interval). */
export const reportFileName = (prefix: string, search: AnalyticsPeriodSearch): string =>
	`${prefix}_${format(selectedPeriodStart(search.interval, search.periodStart), 'yyyy-MM-dd')}_${search.interval}.csv`

/** Exports the grid (including the pinned totals row) as CSV. */
export function ExportCsvButton({ gridApi, fileName }: { gridApi: GridApi | null; fileName: string }) {
	const { L } = useI18n()
	return (
		<Button
			size='small'
			variant='secondary'
			disabled={!gridApi}
			onClick={() => gridApi?.exportDataAsCsv({ fileName })}
		>
			<Download className='h-3.5 w-3.5' />
			{L.Analytics.Reports.Export()}
		</Button>
	)
}

/**
 * A metric cell: the numbers ("100 → 123") are pinned to the left and the delta badge to the right,
 * with the free space between them. Keeping the amounts and badges on fixed edges makes them line up
 * across rows so the column reads as two calm vertical tracks instead of a ragged right-packed block.
 */
export function MetricCell({
	value,
	compared,
	format: fmt
}: {
	value: number | null | undefined
	compared: number | null | undefined
	format: (value: number) => string
}) {
	return (
		<div className='flex h-full items-center justify-between gap-1.5'>
			<span className='tabular-nums'>
				{compared != null && (
					<span className='text-ui-fg-subtle'>
						{fmt(compared)}
						<span className='text-ui-fg-muted'> → </span>
					</span>
				)}
				{value == null ? '—' : fmt(value)}
			</span>
			{compared != null && value != null && <DeltaBadge current={value} previous={compared} />}
		</div>
	)
}

/** Quiet popover with a checkbox per column; at least one column stays selected. */
export function ColumnsPicker({
	options,
	value,
	onChange,
	className
}: {
	options: Array<{ id: string; label: string }>
	value: string[]
	onChange: (ids: string[]) => void
	/** Width/layout override so it can line up with the sibling toolbar controls. */
	className?: string
}) {
	const { L } = useI18n()
	const [open, setOpen] = useState(false)
	const toggle = (id: string) => {
		if (value.includes(id)) {
			if (value.length > 1) onChange(value.filter(v => v !== id))
		} else {
			// Preserve the canonical option order so columns don't jump around.
			onChange(options.filter(o => value.includes(o.id) || o.id === id).map(o => o.id))
		}
	}
	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger
				className={clx(
					'bg-ui-bg-field shadow-buttons-neutral transition-fg flex h-8 select-none items-center justify-between gap-2 rounded-md px-2 py-1.5 outline-none',
					'txt-compact-small text-ui-fg-base hover:bg-ui-bg-field-hover',
					'focus-visible:shadow-borders-interactive-with-active data-[state=open]:shadow-borders-interactive-with-active',
					className
				)}
			>
				<span className='truncate'>{L.Analytics.Reports.ColumnsPicker()}</span>
				<span className='flex shrink-0 items-center gap-1'>
					<Text size='small' className='text-ui-fg-muted'>
						{value.length}
					</Text>
					<TrianglesMini className='shrink-0 text-ui-fg-muted' />
				</span>
			</PopoverTrigger>
			<PopoverContent align='start' className='w-[13rem] p-1'>
				{options.map(option => (
					<button
						key={option.id}
						type='button'
						onClick={() => toggle(option.id)}
						className='txt-compact-small flex w-full items-center gap-x-2 rounded-[4px] px-2 py-1.5 text-left outline-none transition-colors hover:bg-ui-bg-base-hover focus-visible:bg-ui-bg-base-hover'
					>
						<Checkbox checked={value.includes(option.id)} />
						<span className='truncate'>{option.label}</span>
					</button>
				))}
			</PopoverContent>
		</Popover>
	)
}
